import { cn } from '@repo/tailwind-config/tailwind.functions'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DetailedList, DetailedListDetail } from '@repo/ui/detailed-list'
import { InformationIcon, PassportIcon, PinIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getCountryRequiredDocuments } from '../../../api/countries/get-country-required-documents'
import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { getEnrollmentIdentity } from '../../../api/enrollments/get-enrollment-identity'
import { submitEnrollmentIdentityForUSA } from '../../../api/enrollments/submit-enrollment-identity-for-usa'
import { updateEnrollmentIdentity } from '../../../api/enrollments/update-enrollment-identity'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { mapEnrollmentToEnrollmentIdentityInput } from '../../../components/pages/language-test/client-space/client-space-enrollment.utils'
import { ClientEnrollmentIdentity } from '../../../components/pages/language-test/client-space/enrollment-identity'
import { EnrollmentIdentityForm } from '../../../components/pages/language-test/client-space/enrollment-identity.form'
import { ClientHeader } from '../../../components/pages/language-test/client-space/shared-client-space-ui'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { enrollmentChildIdentityBaseFields } from '../../../lib/types/fields/enrollments/enrollment'
import {
  EnrollmentIdentityBase,
  enrollmentIdentityBaseFields
} from '../../../lib/types/fields/enrollments/enrollment-identity'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentIdentityInput } from '../../../lib/types/models/enrollment/enrollment-identity'
import { UpdateInput } from '../../../lib/types/utils'
import { parseError, showAxiosError } from '../../../lib/utils/functions/misc.functions'

type ModalDataType = {
  errorMessage: string
  data?: UpdateInput<EnrollmentIdentityInput>
}

const pageheaders: string[] = [
  'Merci de compléter le formulaire ci-dessous avec la plus grande vigilance.'
]

const pageDetails: DetailedListDetail[] = [
  {
    text: "Saisir les informations <strong>telles qu'elles figurent sur le passeport que le jeune présentera lors des contrôles d'identité</strong> et formalités de voyages (enregistrement, contrôles de police, etc...)."
  },
  {
    variant: 'destructive',
    text: "En cas d'erreur de saisie ou d'information erronée, le jeune se verra malheureusement refuser le départ et ne pourra pas voyager. Dans ce cas, seule la responsabilité des parents et/ou du jeune participant pourra être engagée. LEC ne pourra être tenu pour responsable des conséquences de l'absence de communication des informations requises ou d'une erreur dans les informations transmises."
  },
  {
    variant: 'destructive',
    text: "Dans le cadre du séjour de votre enfant, nous vous rappelons que l'ensemble des informations ci-dessous, concernant son passeport, doivent être envoyées à LEC au plus tard un mois avant le départ."
  },
  {
    text: "Nous vous conseillons de scanner avant votre départ tous les documents utiles (pièce d'identité, passeport, visa, A.S.T. -Attestation de Sortie de Territoire-, papiers d'identité du responsable légal, etc.) et de les envoyer sur votre boite e-mail. En cas de perte ou de vol, cela faciliterait grandement les démarches administratives."
  }
]

export default function ClientIdentityPage() {
  const params = useParams<{ id: string }>()
  const [enrollmentIdentity, setEnrollmentIdentity] = useState<EnrollmentIdentityBase>()
  const id = Number(params.id)
  const { selectedData, modalState, toggleModal, closeAll } = useModalManager<
    'error' | 'warning',
    ModalDataType
  >(['error', 'warning'])

  const { data: enrollment, refetch } = useQuery({
    queryKey: ['enrollment', id, enrollmentChildIdentityBaseFields],
    queryFn: () => getEnrollment(id, enrollmentChildIdentityBaseFields),
    enabled: !!id
  })

  const { isPending } = useQuery({
    queryKey: ['enrollment-identity', id, enrollmentIdentityBaseFields],
    queryFn: async () => {
      const data = await getEnrollmentIdentity(id, enrollmentIdentityBaseFields)
      setEnrollmentIdentity(data)
      return data
    },
    enabled: !!id
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: UpdateInput<EnrollmentIdentityInput>) =>
      updateEnrollmentIdentity(data, enrollmentIdentityBaseFields),
    onSuccess: (data) => {
      refetch()
      setEnrollmentIdentity(data)
      toast.success("Papiers d'identité de l'enfant mis à jour")
    },
    onError: (error, data) => {
      const parsedError = parseError(error)
      console.error('Error updating enrollment identity:', parsedError)

      if (parsedError && parsedError.status) {
        if (parsedError.status === 'error')
          toggleModal('error', { errorMessage: parsedError.message })
        else if (parsedError.status === 'warning')
          toggleModal('warning', { errorMessage: parsedError.message, data })
        else showAxiosError(error)
      } else showAxiosError(error)
    }
  })

  const { mutateAsync: submitEnrollmentIdentityForUSAAsync } = useMutation({
    mutationFn: (data: UpdateInput<EnrollmentIdentityInput>) =>
      submitEnrollmentIdentityForUSA(data, enrollmentIdentityBaseFields),
    onError: showAxiosError,
    onSuccess: (data) => {
      refetch()
      setEnrollmentIdentity(data)
      closeAll()
      toast.success("Papiers d'identité de l'enfant mis à jour")
    }
  })

  // the destination decides the required documents; an OTO Individual stay has no journey
  const country = EnrollmentProperties.isOtoIndividual(enrollment)
    ? enrollment?.otoIndividualProposition?.product.country
    : enrollment?.mainProposition?.journey?.date.product.country

  // isLoading, not isPending: a query left disabled (no country) must not hang the page
  const { data: requiredDocuments, isLoading: isRequiredDocumentsLoading } = useQuery({
    queryKey: ['required-documents', country?.id],
    queryFn: () => {
      if (!country?.id) return undefined
      return getCountryRequiredDocuments(country.id)
    },
    enabled: !!country?.id
  })

  const isSubmitted = enrollment?.childIdentityPapersSubmitted
  const defaultValues = useMemo(
    () =>
      enrollment
        ? mapEnrollmentToEnrollmentIdentityInput(
            enrollment,
            requiredDocuments ?? [],
            enrollmentIdentity
          )
        : undefined,
    [enrollment, requiredDocuments, enrollmentIdentity]
  )

  if (!enrollment || isPending || isRequiredDocumentsLoading) return <LoadingScreen />

  return (
    <div className={'flex flex-col gap-4'}>
      <div
        className={cn('flex mobile:flex-col flex-row gap-4 items-center mobile:items-start', {
          'flex-col items-start': isSubmitted
        })}
      >
        <ClientHeader title={"Papiers d'identité de l'enfant"} icon={PassportIcon} />

        {isSubmitted && (
          <Badge
            variant={'success'}
            size="medium"
            className="py-3 h-fit"
            text={
              'Merci, nous avons bien reçu les informations de papiers d’identité de votre enfant. Il ne vous est plus possible de les modifier. Si vous vous rendez compte d’un problème avec les informations ci-dessous, merci de nous contacter par téléphone au 01.42.67.12.00.'
            }
          />
        )}
      </div>

      {isSubmitted ? (
        <ClientEnrollmentIdentity enrollmentId={enrollment.id} />
      ) : (
        <>
          <DetailedList icon={PinIcon} headers={pageheaders} details={pageDetails} />

          <EnrollmentIdentityForm
            requiredDocuments={requiredDocuments}
            defaultValues={defaultValues}
            handleSubmit={async (values) => {
              await mutateAsync({ id, ...values })
            }}
          />

          {selectedData && (
            <>
              <Modal
                onCancel={() => closeAll()}
                isOpen={modalState.error}
                variant="destructive"
                icon={InformationIcon}
                title={selectedData.errorMessage}
                size="medium"
                hideActions
                customActions={
                  <div className="flex gap-4 justify-center">
                    <Button
                      className="w-full"
                      label="Annuler"
                      variant="ghost"
                      onClick={() => closeAll()}
                    />
                  </div>
                }
              />

              <Modal
                onCancel={() => closeAll()}
                isOpen={modalState.warning}
                variant="destructive"
                icon={InformationIcon}
                title={selectedData.errorMessage}
                size="medium"
                hideActions
                customActions={
                  <div className="flex gap-4 justify-center">
                    <Button
                      className="w-full"
                      label="Annuler"
                      variant="ghost"
                      onClick={() => closeAll()}
                    />
                    <Button
                      className="w-full"
                      label="Confirmer quand-même"
                      variant="primary"
                      onClick={async () => {
                        await submitEnrollmentIdentityForUSAAsync(selectedData.data!)
                      }}
                    />
                  </div>
                }
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
